import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, UserPlus } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export default function EmployeeNew() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { theme } = useTheme();
  
  const [formData, setFormData] = useState({
    matricule: '',
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    poste: '',
    type_contrat: 'CDI',
    date_embauche: new Date().toISOString().split('T')[0],
    jours_max_semaine: 5,
    station: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Generate matricule if empty
      const dataToSend = { ...formData };
      if (!dataToSend.matricule) {
        dataToSend.matricule = `MAT${Date.now().toString().slice(-6)}`;
      }
      
      await api.post('/employees', dataToSend);
      toast.success('Salarié créé avec succès');
      navigate('/employees');
    } catch (error) {
      console.error('Error creating employee:', error);
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erreur lors de la création du salarié');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6 px-1 py-4 md:p-0 pb-24 md:pb-0" data-testid="employee-new-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          onClick={() => navigate('/employees')}
          className={cn(
            'border',
            theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white hover:bg-white/10' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
          )}
          data-testid="back-button"
        >
          <ArrowLeft size={18} className="mr-2" />
          Retour
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-500/20 rounded-lg">
            <UserPlus size={24} className="text-red-500" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold">Nouveau Salarié</h2>
            <p className={theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'}>Remplissez les informations du salarié</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className={cn(
        'p-6 max-w-3xl rounded-xl border overflow-hidden',
        theme === 'dark' ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200 shadow-sm'
      )}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section Identité */}
          <div>
            <h3 className={cn(
              'text-lg font-semibold mb-4 pb-2 border-b',
              theme === 'dark' ? 'border-white/10' : 'border-gray-200'
            )}>Identité</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="matricule" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Matricule</Label>
                <Input
                  id="matricule"
                  value={formData.matricule}
                  onChange={(e) => handleChange('matricule', e.target.value)}
                  placeholder="Auto-généré si vide"
                  className={cn(
                    'mt-1',
                    theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white placeholder:text-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400'
                  )}
                  data-testid="input-matricule"
                />
              </div>
              <div>
                <Label htmlFor="prenom" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Prénom *</Label>
                <Input
                  id="prenom"
                  value={formData.prenom}
                  onChange={(e) => handleChange('prenom', e.target.value)}
                  placeholder="Jean"
                  required
                  className={cn(
                    'mt-1',
                    theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white placeholder:text-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400'
                  )}
                  data-testid="input-prenom"
                />
              </div>
              <div>
                <Label htmlFor="nom" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Nom *</Label>
                <Input
                  id="nom"
                  value={formData.nom}
                  onChange={(e) => handleChange('nom', e.target.value)}
                  placeholder="Dupont"
                  required
                  className={cn(
                    'mt-1',
                    theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white placeholder:text-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400'
                  )}
                  data-testid="input-nom"
                />
              </div>
              <div>
                <Label htmlFor="poste" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Poste *</Label>
                <Select
                  value={formData.poste}
                  onValueChange={(value) => handleChange('poste', value)}
                >
                  <SelectTrigger className={cn(
                    'mt-1',
                    theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'
                  )} data-testid="select-poste">
                    <SelectValue placeholder="Sélectionner un poste" />
                  </SelectTrigger>
                  <SelectContent className={theme === 'dark' ? 'bg-zinc-800 border-white/10' : 'bg-white border-gray-200'}>
                    <SelectItem value="Chauffeur PL">Chauffeur PL</SelectItem>
                    <SelectItem value="Chauffeur SPL">Chauffeur SPL</SelectItem>
                    <SelectItem value="Chauffeur VL">Chauffeur VL</SelectItem>
                    <SelectItem value="Responsable exploitation">Responsable exploitation</SelectItem>
                    <SelectItem value="Responsable parc">Responsable parc</SelectItem>
                    <SelectItem value="Chef d'équipe">Chef d'équipe</SelectItem>
                    <SelectItem value="Administratif">Administratif</SelectItem>
                    <SelectItem value="Autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Section Contact */}
          <div>
            <h3 className={cn(
              'text-lg font-semibold mb-4 pb-2 border-b',
              theme === 'dark' ? 'border-white/10' : 'border-gray-200'
            )}>Contact</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="email@exemple.com"
                  required
                  className={cn(
                    'mt-1',
                    theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white placeholder:text-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400'
                  )}
                  data-testid="input-email"
                />
              </div>
              <div>
                <Label htmlFor="telephone" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Téléphone *</Label>
                <Input
                  id="telephone"
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => handleChange('telephone', e.target.value)}
                  placeholder="06 12 34 56 78"
                  required
                  className={cn(
                    'mt-1',
                    theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white placeholder:text-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400'
                  )}
                  data-testid="input-telephone"
                />
              </div>
            </div>
          </div>

          {/* Section Contrat */}
          <div>
            <h3 className={cn(
              'text-lg font-semibold mb-4 pb-2 border-b',
              theme === 'dark' ? 'border-white/10' : 'border-gray-200'
            )}>Contrat</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="type_contrat" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Type de contrat *</Label>
                <Select
                  value={formData.type_contrat}
                  onValueChange={(value) => handleChange('type_contrat', value)}
                >
                  <SelectTrigger className={cn(
                    'mt-1 w-full',
                    theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'
                  )} data-testid="select-contrat">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={theme === 'dark' ? 'bg-zinc-800 border-white/10' : 'bg-white border-gray-200'}>
                    <SelectItem value="CDI">CDI</SelectItem>
                    <SelectItem value="CDD">CDD</SelectItem>
                    <SelectItem value="Intérim">Intérim</SelectItem>
                    <SelectItem value="Apprentissage">Apprentissage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col items-center">
                <Label htmlFor="date_embauche" className={cn('text-center', theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>Date d'embauche *</Label>
                <Input
                  id="date_embauche"
                  type="date"
                  value={formData.date_embauche}
                  onChange={(e) => handleChange('date_embauche', e.target.value)}
                  required
                  className={cn(
                    'mt-1',
                    theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white [&::-webkit-calendar-picker-indicator]:invert' : 'bg-white border-gray-300 text-gray-900'
                  )}
                  data-testid="input-date-embauche"
                />
              </div>
              <div>
                <Label htmlFor="station" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Station *</Label>
                <Select
                  value={formData.station}
                  onValueChange={(value) => handleChange('station', value)}
                >
                  <SelectTrigger className={cn(
                    'mt-1 w-full',
                    theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'
                  )} data-testid="select-station">
                    <SelectValue placeholder="Sélectionner une station" />
                  </SelectTrigger>
                  <SelectContent className={theme === 'dark' ? 'bg-zinc-800 border-white/10' : 'bg-white border-gray-200'}>
                    <SelectItem value="CHRONO TLS">CHRONO TLS</SelectItem>
                    <SelectItem value="CHRONO LIG">CHRONO LIG</SelectItem>
                    <SelectItem value="CHRONO PAU">CHRONO PAU</SelectItem>
                    <SelectItem value="FEDEX">FEDEX</SelectItem>
                    <SelectItem value="AMAZON DAC2">AMAZON DAC2</SelectItem>
                    <SelectItem value="AUTRES">AUTRES</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="jours_max_semaine" className={theme === 'dark' ? 'text-zinc-300' : 'text-gray-700'}>Jours/semaine max</Label>
                <Input
                  id="jours_max_semaine"
                  type="number"
                  min="1"
                  max="7"
                  value={formData.jours_max_semaine}
                  onChange={(e) => handleChange('jours_max_semaine', parseInt(e.target.value) || 5)}
                  className={cn(
                    'mt-1 w-full',
                    theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'
                  )}
                  data-testid="input-jours-max"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className={cn(
            'flex justify-end gap-3 pt-4 border-t',
            theme === 'dark' ? 'border-white/10' : 'border-gray-200'
          )}>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/employees')}
              className={cn(
                theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white hover:bg-white/10' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
              )}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading}
              variant="brand"
              data-testid="submit-employee"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Création...
                </span>
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  Créer le salarié
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
